import {getEventId} from '@wix/wix-events-commons-statics'
import classNames from 'classnames'
import React, {useState} from 'react'
import {useWidgetActions, useWidgetHost, useWidgetState} from '../../../../../hooks/state-provider'
import {
  getDayNumber,
  getEventsForDay,
  getSelectedDate,
  getToday,
  isDayInCurrentMonth,
} from '../../../../../selectors/calendar-layout'
import {getActionBackground, getBorderStyle, getOtherMonthBackground} from '../../../../../utils/calendar'
import s from './calendar-cell.scss'
import {DayEvents} from './day-events'
import {CalendarCellProps} from './interfaces'

export const CalendarCell = ({registerContainerRef, week, weekDay, timestamp}: CalendarCellProps) => {
  const [hover, setHover] = useState(false)
  const {openMonthlyCalendarEvent, openMonthlyCalendarPopup, closeMonthlyCalendarPopup} = useWidgetActions()
  const events = useWidgetState(state => getEventsForDay(state, timestamp))

  const todayDate = useWidgetState(getToday)
  const selectedDate = useWidgetState(getSelectedDate)

  const past = timestamp < todayDate
  const today = timestamp === todayDate
  const active = timestamp === selectedDate
  const dayInCurrentMonth = useWidgetState(state => isDayInCurrentMonth(state, timestamp))
  const dayNumber = useWidgetState(state => getDayNumber(state, timestamp))

  const selectionExists = Boolean(selectedDate)

  const host = useWidgetHost()
  const borderStyle = getBorderStyle({host})
  const actionBackground = getActionBackground({host})
  const otherMonthBackground = getOtherMonthBackground({host})

  const handleRegisterContainerRef = (ref: HTMLElement) => {
    registerContainerRef(week, weekDay, ref)
  }

  const openPopup = () => {
    if (events.length === 1) {
      openMonthlyCalendarEvent({eventId: getEventId(events[0]), selectedDate: timestamp})
    } else {
      openMonthlyCalendarPopup(timestamp)
    }
  }

  const handleMouseOver = () => {
    setHover(true)
  }

  const handleMouseOut = () => {
    setHover(false)
  }

  const handleCellClick = () => {
    events.length ? openPopup() : closeMonthlyCalendarPopup()
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleCellClick()
    }
  }

  const useActionBackground = events.length && (active || hover)

  const style = {
    ...((!today || selectionExists) && !active ? borderStyle : {}),
    ...((!dayInCurrentMonth || past) && !useActionBackground ? otherMonthBackground : {}),
    ...(useActionBackground ? actionBackground : {}),
  }

  return (
    <div
      className={classNames(s.cell, {
        [s.today]: today && !selectionExists,
        [s.withEvents]: events.length,
        [s.active]: active,
      })}
      style={style}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={handleCellClick}
      onKeyPress={handleKeyPress}
      tabIndex={events.length ? 0 : undefined}
      data-hook={dayInCurrentMonth ? `calendar-day-${dayNumber}` : `calendar-other-month-day-${dayNumber}`}
    >
      <div ref={handleRegisterContainerRef} className={s.content}>
        <div
          className={classNames(s.day, {[s.otherMonth]: !dayInCurrentMonth, [s.today]: today})}
          data-hook={dayInCurrentMonth ? `day-of-month-${dayNumber}` : `day-of-other-month-${dayNumber}`}
        >
          {dayNumber}
        </div>
        {events.length ? <DayEvents events={events} currentDate={timestamp} /> : null}
      </div>
      <div className={s.border} />
    </div>
  )
}
